<script lang="ts" setup>
import {
  PopoverColorEnum,
  PopoverPositionEnum,
  TirPmPopover,
  TirPmInput,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmColorPalette,
  TirPmTooltipElement,
} from 'tir-components';
import { ChartBarIcon, CrossCompactIcon, CheckIcon } from 'tir-style-system/icons/outline';
import { PaintIcon } from 'tir-style-system/icons/solid';
import { computed, PropType, ref } from 'vue';
import { TaskParameterModel } from '../models';
import { getTaskParameterPalette } from '../services';
import { TaskParameterTypeEnum } from '../enums';

/**Параметры */
const props = defineProps({
  /**Данные о настройке */
  data: { type: Object as PropType<TaskParameterModel>, default: () => new TaskParameterModel() },
  /**Скрыть удаление */
  hideRemove: { type: Boolean },
  /**Демонстрационный тег */
  preview: { type: Boolean, default: false },
  /**Редактирование */
  isEditing: { type: Boolean, default: false },
  /**Укороченная информация параметра */
  shortInfoParameter: { type: Boolean, default: false },
});
/**События */
const emit = defineEmits<{
  /**Событие удаления параметра */
  (e: 'remove'): void;
}>();
/**Активно редактирование */
const isEditing = ref(false);
/**Новое имя параметра */
const newName = ref();
/**Активно наведение */
const isHover = ref(false);
/**Сфокусирован ли какой то параметр */
const isFocus = ref(false);

/**Цвет текста */
const textColor = computed(() => ({ color: props.data.Color }));
/**Стиль фона иконки */
const iconBackgroundStyle = computed(() => ({ 'background-color': props.data.Color }));

/**Переключение флага isHover */
const setHover = (_isHover) => {
  isHover.value = isEditing.value ? false : _isHover;
};
/**Переключение редактирования */
const editingParameter = (value) => {
  if (!props.preview) isEditing.value = value;
};
/**Смена имени параметра */
const changeName = (parameter: TaskParameterModel, name: string | number) => {
  parameter.Name = String(name);
  isEditing.value = false;
};
/**Запись имени параметра */
const setName = (value) => {
  newName.value = value;
};
/**Сменить цвет параметра */
const changeParameterColor = (parameter: TaskParameterModel, color: string) => {
  parameter.Color = color;
};
/**Есть фокус на поле ввода или открыто окно выбора цвета */
const setFocus = (_isFocus: boolean) => {
  isFocus.value = _isFocus;
};
</script>
<template>
  <div class="difficulty-parameter" @mouseenter="setHover(true)" @mouseleave="setHover(false)">
    <div class="difficulty-parameter_icon" :style="textColor">
      <TirPmTooltipElement>
        <ChartBarIcon />

        <template #content>
          <div class="difficulty-parameter__popover-title">{{ props.data.Name }}</div>
        </template>
      </TirPmTooltipElement>
      <div class="difficulty-parameter_icon_background" />
    </div>
    <div class="difficulty-parameter_name">
      <span
        v-if="!isEditing && !shortInfoParameter"
        v-text="props.data.Name"
        :style="textColor"
        @dblclick="editingParameter(true)"
      />
      <div class="difficulty-parameter_input-block" v-if="isEditing && !preview">
        <TirPmInput
          class="difficulty-parameter_input"
          :color="props.data.Color"
          :is-with-hint="false"
          @focus="editingParameter(true)"
          @blur="editingParameter(false)"
          @keyup.enter="changeName(props.data, newName ?? props.data.Name), editingParameter(false)"
          @keyup.esc="editingParameter(false)"
          :modelValue="data.Name"
          @input="setName($event)"
          isMicro
          autoFocus
        />
        <div :style="{ background: textColor.color }" class="difficulty-parameter_divider" />
        <TirPmButton
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Small"
          @mousedown.prevent
          @click.stop="changeName(props.data, newName ?? props.data.Name)"
          class="difficulty-parameter_icon"
        >
          <template #leftSlot>
            <CheckIcon class="difficulty-parameter_icon" />
          </template>
        </TirPmButton>
        <TirPmButton
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Small"
          @mousedown.prevent
          @click.stop="editingParameter(false)"
          class="difficulty-parameter_icon"
        >
          <template #leftSlot>
            <CrossCompactIcon class="difficulty-parameter_icon" />
          </template>
        </TirPmButton>
      </div>
      <div v-if="(isHover || isFocus) && !preview" class="difficulty-parameter_color-change">
        <div :style="{ background: textColor.color }" class="difficulty-parameter_divider" />
        <TirPmColorPalette
          class="difficulty-parameter_color"
          :model-value="props.data.Color"
          :position="PopoverPositionEnum.Bottom"
          :colors="getTaskParameterPalette(TaskParameterTypeEnum.Difficulty)"
          @update:model-value="changeParameterColor(props.data, $event)"
          @open="setFocus(true)"
          @close="setFocus(false)"
        >
          <template #icon="slot">
            <div class="difficulty-parameter_color" @mousedown.stop>
              <PaintIcon :style="{ color: slot.color }" />
            </div>
          </template>
        </TirPmColorPalette>
      </div>
    </div>
    <div
      v-if="!hideRemove"
      class="difficulty-parameter_delete"
      :style="textColor"
      @click.stop="emit('remove')"
    >
      <CrossCompactIcon />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.difficulty-parameter {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  text-wrap: nowrap;
  height: 1.25rem;
  cursor: pointer;

  &__popover-title {
    display: flex;
    justify-content: center;
    overflow-wrap: break-word;
    font-size: 0.75rem;
  }

  &_icon {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 1.25rem;
    border-radius: 0.25rem;
    overflow: hidden;
    margin-right: 0.5rem;
    svg {
      position: relative;
      z-index: 1;
      width: 0.875rem !important;
      height: 0.875rem !important;
      min-width: 0.875rem !important;
      min-height: 0.875rem !important;
    }
    &_background {
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      opacity: 0.1;
    }
  }
  &_name {
    font-size: 0.75rem;
    font-weight: 400;
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }
  &_delete {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
  }
  &_input-block {
    display: flex;
    align-items: center;
  }
  &_icon {
    width: 1rem;
    height: 1rem;
    color: var(--text-primary, #272d37);
  }
  &_color-change {
    display: flex;
    align-items: center;
  }
  &_color {
    width: 0.917rem;
    height: 0.917rem;
  }
}
</style>
<style lang="scss">
.difficulty-parameter_input {
  .input-container {
    border: none;
    height: 1.25rem !important;
    background-color: transparent;
    &:hover {
      border: none;
    }
  }
  .input-container__input-wrapper {
    margin: 0;
    height: 1.25rem !important;
  }
  .input-container__input {
    line-height: 1.25rem !important;
    font-size: 0.75rem;
    font-weight: 400;
    margin: 0;
  }
}
</style>
